import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";

export default function SchoolSubscriptionSettings() {
  const api = useApiClient();
  const { updateUserInfo } = useAuthenticationStore();
  const [configuration, setConfiguration] = useState(null);
  const [options, setOptions] = useState({
    allActiveSubjects: [],
    allYearGroups: [],
  });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const request = useRef(0);

  const load = async () => {
    const current = ++request.current;
    setLoading(true);
    setLoadError("");
    try {
      const [data, choices] = await Promise.all([
        api.schools.getAccountSchoolSubscription(),
        api.core.getAllActiveSubjects(),
      ]);
      if (current !== request.current) return;
      setConfiguration(data);
      setRows(
        data?.licences.map((row) => ({
          ...row,
          subjects: [...row.subjects],
        })) || [],
      );
      setOptions(choices);
      if (!data) setOpen(false);
    } catch (error) {
      if (current === request.current)
        setLoadError(error.message || "Unable to load school subscription.");
    } finally {
      if (current === request.current) setLoading(false);
    }
  };
  useEffect(() => {
    load();
    return () => {
      request.current += 1;
    };
  }, [api]);
  const valid = rows.length > 0 && rows.every((row) => row.subjects.length > 0);
  const save = async () => {
    if (busy.current || loading || loadError || !valid) return;
    busy.current = true;
    setSaving(true);
    const current = request.current;
    try {
      const result = await api.schools.manageAccountSchoolSubscription({
        revision: configuration.revision,
        licenseItems: rows.map((row) => ({
          yearGroup: row.yearGroup.id,
          subjects: row.subjects.map((subject) => subject.id),
        })),
      });
      if (current !== request.current) return;
      enqueueSnackbar(result.message, {
        variant: result.success ? "success" : "error",
      });
      if (result.success) {
        setOpen(false);
        await load();
        try {
          const userInfo = await api.users.me();
          if (current + 1 === request.current) updateUserInfo(userInfo);
        } catch (_error) {
          if (current + 1 === request.current)
            enqueueSnackbar(
              "Changes saved. Refresh your account to reload its details.",
              { variant: "warning" },
            );
        }
      }
    } catch (error) {
      if (current === request.current)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  if (!configuration && !loading && !loadError) return null;
  return (
    <Card variant="shadow">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h3">School subscription</Typography>
          {loading && !open && (
            <Typography>Loading school subscription…</Typography>
          )}
          {loadError && !open && (
            <>
              <Typography color="error">{loadError}</Typography>
              <Button onClick={load}>Retry</Button>
            </>
          )}
          {configuration && (
            <>
              <Typography>{configuration.schoolName}</Typography>
              {configuration.licences.map((row) => (
                <Typography key={row.yearGroup.id}>
                  Year {row.yearGroup.yearGroup}:{" "}
                  {row.subjects.map((subject) => subject.name).join(", ") ||
                    "No subjects enabled"}
                </Typography>
              ))}
              <Button
                variant="outlined"
                disabled={loading || !!loadError || saving}
                onClick={() => {
                  setOpen(true);
                  load();
                }}
              >
                Manage subjects and year groups
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy.current) setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage subjects and year groups</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              Changes apply to the whole school, including other teachers.
              Removed subjects and year groups will no longer be available under
              the school licence.
            </Typography>
            <Typography variant="body2">
              Keep at least one year group and one subject in each year group.
            </Typography>
            {loading && <Typography>Loading configuration…</Typography>}
            {loadError && <Typography color="error">{loadError}</Typography>}
            {rows.map((row, index) => {
              const existing =
                configuration?.licences.find(
                  (item) => item.yearGroup.id === row.yearGroup.id,
                )?.subjects || [];
              const subjects = [
                ...new Map(
                  [...options.allActiveSubjects, ...existing].map((subject) => [
                    subject.id,
                    subject,
                  ]),
                ).values(),
              ];
              return (
                <Stack key={row.yearGroup.id} spacing={1}>
                  <Typography fontWeight="bold">
                    Year {row.yearGroup.yearGroup}
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={subjects}
                    value={row.subjects}
                    disabled={saving || loading}
                    getOptionLabel={(subject) => subject.name}
                    isOptionEqualToValue={(left, right) => left.id === right.id}
                    onChange={(_, selected) =>
                      setRows((previous) =>
                        previous.map((item, i) =>
                          i === index ? { ...item, subjects: selected } : item,
                        ),
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Subjects for year ${row.yearGroup.yearGroup}`}
                        error={!row.subjects.length}
                        helperText={
                          !row.subjects.length
                            ? "Select at least one subject."
                            : ""
                        }
                      />
                    )}
                  />
                  <Button
                    disabled={saving || loading || rows.length === 1}
                    onClick={() =>
                      setRows((previous) =>
                        previous.filter((_, i) => i !== index),
                      )
                    }
                  >
                    Remove year {row.yearGroup.yearGroup}
                  </Button>
                </Stack>
              );
            })}
            <TextField
              select
              label="Add year group"
              value=""
              disabled={saving || loading}
              onChange={(event) => {
                const year = options.allYearGroups.find(
                  (item) => item.id === event.target.value,
                );
                setRows((previous) => [
                  ...previous,
                  { yearGroup: year, subjects: [] },
                ]);
              }}
            >
              {options.allYearGroups
                .filter(
                  (year) => !rows.some((row) => row.yearGroup.id === year.id),
                )
                .map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    Year {year.yearGroup}
                  </MenuItem>
                ))}
            </TextField>
            <Button disabled={saving || loading} onClick={load}>
              Reload configuration
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || loading || !!loadError || !valid}
            onClick={save}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
